<template>
   <div v-if="createdOrderId" class="order-created" data-cy="kit-already-ordered">
      <h2 class="block-question-title">{{ $t("dna-testing.order_created") }}</h2>
      <p class="block-question-subtitle" data-cy="ordered-order-id">#{{ createdOrderId }}</p>
      <p class="block-question-subtitle" data-cy="ordered-delivery-name">
         {{ shippingAddress?.deliveryName }}
      </p>
      <address class="delivery-address" data-cy="ordered-delivery-address">
         {{ shippingAddress?.line_1 }},<br />
         <span v-if="shippingAddress?.line_2">{{ shippingAddress?.line_2 }},<br /></span>
         {{ shippingAddress?.city }},<br />
         {{ shippingAddress?.region }},<br />
         {{ shippingAddress?.postalCode }},<br />
         {{ shippingAddress?.country }}
      </address>
   </div>
   <div v-else data-cy="kitlink-ordering">
      <RequestLoader :isLoading="isLoading" data-cy="kit-order-block" class="form-min-heights">
         <TextBox
            id="delivery-name"
            v-model.trim="userInput.deliveryName"
            class="margin-bottom"
            :label="$t('user-data.address.delivery_name')"
            :hasError="showBlockErrors && $v.deliveryName.$error"
            :errorHelper="deliveryHelperData"
            @change="processInputs"
         />
         <TextBox
            id="line1"
            v-model.trim="userInput.line_1"
            class="margin-bottom"
            :label="$t('user-data.address.line_1')"
            :hasError="showBlockErrors && $v.line_1.$error"
            :errorHelper="line1HelperData"
            @change="processInputs"
         />
         <TextBox
            id="line2"
            v-model.trim="userInput.line_2"
            class="margin-bottom"
            :label="$t('user-data.address.line_2')"
            :showIsOptional="true"
            :hasError="showBlockErrors && $v.line_2.$error"
            :errorHelper="line2HelperData"
            @change="processInputs"
         />
         <div class="grid layout-cols">
            <TextBox
               id="city"
               v-model.trim="userInput.city"
               class="margin-bottom"
               :label="$t('user-data.address.city')"
               :hasError="showBlockErrors && $v.city.$error"
               :errorHelper="cityHelperData"
               @change="processInputs"
            />
            <TextBox
               id="region"
               v-model.trim="userInput.region"
               class="margin-bottom"
               :label="$t('user-data.address.region')"
               :hasError="showBlockErrors && $v.region.$error"
               :errorHelper="regionHelperData"
               @change="processInputs"
            />
         </div>
         <div class="grid layout-cols">
            <TextBox
               id="postCode"
               v-model.trim="userInput.postalCode"
               class="margin-bottom"
               :label="$t('user-data.address.postcode')"
               :hasError="showBlockErrors && $v.postalCode.$error"
               :errorHelper="postalCodeHelperData"
               @change="processInputs"
            />
            <SimpleSelect
               :selectedOption="userInput.country"
               :disabled="!!props.block.data.settings.lockToCountry"
               :label="$t('user-data.address.country')"
               :options="countryOptions"
               :errorHelper="countryHelperData"
               :hasError="showBlockErrors && $v.country.$error"
               @option-selected="updateCountry"
            />
         </div>
      </RequestLoader>
   </div>
</template>

<script setup lang="ts">
import CountriesApi, { type Country } from "@/data/api-layer/countriesApi";
import { required, maxLength, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { useI18n } from "@/vue-i18n/vue-i18n";
import SimpleSelect from "@/components/inputs/SimpleSelect.vue";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import { useUserStore } from "@/stores/userStore";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";

// interface for block.data
export interface KitLinkOrderingAnswerData {
   shippingAddress: ShippingAddress;
}
export interface ShippingAddress {
   deliveryName: string;
   line_1: string;
   line_2: string;
   city: string;
   country: string;
   postalCode: string;
   region: string;
}
export interface KitLinkOrderingBlock extends FlowselBlock {
   data: {
      settings: {
         lockToCountry: string;
      };
      shippingAddress: ShippingAddress;
   };
}
interface Props {
   block: KitLinkOrderingBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// declarations
// -----------------------------------------
const { t: $t } = useI18n();
const emit = defineEmits(["update-valid", "update-answer"]);
const userStore = useUserStore();

const props = defineProps<Props>();
const isLoading = ref(true);
const countryList = ref<Country[]>([]);
const userInput = ref<ShippingAddress>({
   deliveryName: "",
   line_1: "",
   line_2: "",
   city: "",
   region: "",
   postalCode: "",
   country: "",
});

// validation
// -----------------------------------------
const rules = {
   deliveryName: {
      required: helpers.withMessage($t("validation.field_required"), required),
      maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(250)),
      $autoDirty: true,
   },
   line_1: {
      required: helpers.withMessage($t("validation.field_required"), required),
      maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(250)),
      $autoDirty: true,
   },
   line_2: {
      maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(250)),
      $autoDirty: true,
   },
   city: {
      required: helpers.withMessage($t("validation.field_required"), required),
      maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(250)),
      $autoDirty: true,
   },
   region: {
      required: helpers.withMessage($t("validation.field_required"), required),
      maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(250)),
      $autoDirty: true,
   },
   postalCode: {
      required: helpers.withMessage($t("validation.field_required"), required),
      maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(10)),
      $autoDirty: true,
   },
   country: {
      required: helpers.withMessage($t("validation.field_required"), required),
      $autoDirty: true,
   },
};
const $v: any = useVuelidate(rules, userInput);

// lifecycle
// -----------------------------------------
onMounted(async () => {
   isLoading.value = true;

   try {
      await fetchCountryList();

      // Check userBlockAnswer for a previous response before attempting to use user data to pre-populate
      if (props.userBlockAnswer) {
         initialiseComponent(props.userBlockAnswer.data?.address);
      } else {
         await fetchUserData();
      }
   } catch (error) {
      console.error("Failed to fetch mount data for kit-ordering block: ", error);
   } finally {
      isLoading.value = false;
   }
});

// computed
// -----------------------------------------
const shippingAddress = computed(() => props.userBlockAnswer.data?.shippingAddress);

const countryOptions = computed(() => {
   // re-map our country codes to an array suitable for SimpleSelect component
   return countryList.value.map(({ alpha_2, name }) => ({ label: name, value: alpha_2 }));
});

const createdOrderId = computed(() => {
   return props.userBlockAnswer?.metadata?.orderId;
});

// Textbox helper data
const deliveryHelperData = computed(() => {
   return getTextboxHelperData("deliveryName");
});
const line1HelperData = computed(() => {
   return getTextboxHelperData("line_1");
});
const line2HelperData = computed(() => {
   return getTextboxHelperData("line_2");
});
const cityHelperData = computed(() => {
   return getTextboxHelperData("city");
});
const regionHelperData = computed(() => {
   return getTextboxHelperData("region");
});
const postalCodeHelperData = computed(() => {
   return getTextboxHelperData("postalCode");
});
const countryHelperData = computed(() => {
   return getTextboxHelperData("country");
});

// methods
// -----------------------------------------
/**
 * Try and fetch country list for the country drop down select.
 */
async function fetchCountryList() {
   try {
      const countryResp = await CountriesApi.getCountries();
      countryList.value = countryResp;
   } catch (e) {
      throw e; // Re-throw the error to let `onMounted` handle it
   }
}

// update country select choice
function updateCountry(country) {
   userInput.value.country = country;
   processInputs();
}

function getTextboxHelperData(vuelidate_input) {
   if ($v.value[vuelidate_input].$errors.length === 0) return null;
   return {
      text: $v.value[vuelidate_input].$errors[0].$message,
      code: $v.value[vuelidate_input].$errors[0].$validator,
   };
}

/**
 * Use the fetched user data to pre-populate the fields or set validity if order already submitted.
 */
function initialiseComponent(userData) {
   if (createdOrderId.value) {
      // If the order is already submitted, set the answer value to the current one, and valid to true, to allow a 'next' click
      emit("update-valid", true);

      const { shippingAddress } = props.userBlockAnswer.data;
      const { deliveryName, postalCode, ...rest } = shippingAddress;

      emit("update-answer", {
         shipping_address: {
            ...rest,
            delivery_name: deliveryName,
            postal_code: postalCode,
         },
      });
   } else {
      // no order placed > pre-populate fields
      const newInput = {
         deliveryName: userData.fullname,
         line_1: userData.address?.line_1,
         line_2: userData.address?.line_2,
         city: userData.address?.city,
         region: userData.address?.region,
         postalCode: userData.address?.postalCode,
         country: userData.address?.country,
      };

      // if the settings have locked the country we set the data to that
      if (props.block.data.settings.lockToCountry) {
         newInput.country = props.block.data.settings.lockToCountry;
      }

      // update input state
      userInput.value = newInput;

      // touch vuelidate to trigger validation
      processInputs();
   }
}

async function fetchUserData() {
   try {
      await userStore.setUser();
      initialiseComponent(userStore.user);
   } catch (e) {
      throw e; // Re-throw the error to let `onMounted` handle it
   }
}

function processInputs() {
   if (!$v.value.$dirty) $v.value.$touch();

   if ($v.value.$invalid || $v.value.$error) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid - convert to submissible format
      // This block type cannot be optional, and so a null answer will never be submitted
      emit("update-valid", true);
      emit("update-answer", {
         shipping_address: {
            delivery_name: userInput.value.deliveryName || "",
            line_1: userInput.value.line_1 || "",
            line_2: userInput.value.line_2 || "",
            city: userInput.value.city || "",
            region: userInput.value.region || "",
            postal_code: userInput.value.postalCode || "",
            country: userInput.value.country || "",
         },
      });
   }
}
</script>

<style scoped lang="scss">
$orderCreatedBackgroundColor: var(--grey-10);

//We set the min heights because they make the appearance of this block after loading data much smoother
// Otherwise the block will jump around as the data is loaded
.form-min-heights {
   min-height: 33rem;
}

@media (max-width: 47.9375rem) {
   .form-min-heights {
      min-height: 49rem;
   }
}

.order-created {
   width: 100%;
   background-color: $orderCreatedBackgroundColor;
   padding: 1rem;
}

.block-question-title {
   font-size: 1.25rem;
   font-weight: bold;
}

.block-question-subtitle {
   font-size: 1rem;
   margin-top: 0.5rem;
}

.delivery-address {
   padding-top: 1rem;
   padding-bottom: 1rem;
   font-style: normal;
}

.margin-bottom {
   margin-bottom: 1.5rem;
}

.grid {
   display: grid;
   gap: 1rem; // Adjust spacing between grid items
}

.layout-cols {
   @media (min-width: 1024px) {
      // lg breakpoint
      grid-template-columns: repeat(2, 1fr);
   }
}
</style>
